/**
 * Types for the DS-native member snapshot consumed by the ABO member-password
 * screen. Mirrors the digital-services `MemberSnapshot` returned by
 * `GET /v1/abo/member-account/:email` (read-only; never carries the password).
 */
export interface MemberSnapshotPlan {
  itemPriceId?: string;
  status?: string;
  afyPackages?: string[];
}

export interface MemberSnapshotGhl {
  locationId?: string;
  subdomain?: string;
  status?: string;
}

/**
 * Where DS captured the member's stored password copy (DS
 * `PASSWORD_VAULT_SOURCES`). Typed as an open string so a source added in DS
 * later still renders (ABO falls back to the raw value).
 */
export type PasswordCaptureSource =
  | "abo-set"
  | "hubspot-route"
  | "member-route"
  | "bba-set"
  | "provisioning"
  | "rmm-provisioning"
  | "upsell"
  | "login"
  | "migration"
  | (string & Record<never, never>);

/**
 * Metadata of the member's stored password copy in the DS vault, read without
 * opening it (DS task 111). `current` is `false` when the member's password
 * changed since the copy was captured, so the copy may no longer work.
 */
export interface MemberSnapshotPasswordReveal {
  stored: boolean;
  current: boolean | null;
  capturedAt: string | null;
  source: PasswordCaptureSource | null;
}

export interface MemberSnapshot {
  email: string;
  firstName?: string;
  lastName?: string;
  status: string;
  plan?: MemberSnapshotPlan;
  ghl?: MemberSnapshotGhl;
  /**
   * Whether DS offers the reveal: `true` when its password vault is
   * configured, `false` when it is not. Absent on an older DS, which ABO
   * treats as "available" so the two apps can deploy in either order.
   */
  passwordRevealAvailable?: boolean;
  /** The stored copy's metadata (DS task 111). Absent on an older DS. */
  passwordReveal?: MemberSnapshotPasswordReveal;
}

/**
 * Where in ABO the reveal was requested, recorded by DS in the audit event
 * (ADR-002). Client-declared provenance only: DS never decides on it.
 */
export type PasswordRevealSurface = "header" | "security-tab";

/**
 * What the operator asked for: to see the password or to copy it without
 * displaying it. Recorded by DS in the audit event (ADR-002).
 */
export type PasswordRevealIntent = "reveal" | "copy";

/** The optional inputs of a reveal besides the member e-mail. */
export interface RevealMemberPasswordOptions {
  /** The signed-in operator, sent as `x-afy-user-email` (DS checks the role). */
  operatorEmail: string;
  /**
   * The operator's FusionAuth id token, sent as `x-afy-operator-token` so DS
   * can verify who is revealing (DS task 115). Omitted when empty.
   */
  operatorToken?: string;
  /** Free text stored in the DS audit event only (at most 200 characters). */
  reason?: string;
  /** The ABO surface asking (audit provenance); omitted from the body when unset. */
  surface?: PasswordRevealSurface;
  /** Reveal or copy (audit provenance); omitted from the body when unset. */
  intent?: PasswordRevealIntent;
}

/** The longest reason DS accepts on a reveal. */
export const REVEAL_REASON_MAX_LENGTH = 200;

/**
 * Response of `POST /v1/abo/member-account/reveal-password`. `password` is the
 * member's plaintext; `isLikelyAutoGenerated` flags a probable provisioning
 * placeholder rather than a chosen password. From DS task 111 on, the value is
 * opened from the DS vault (`source: 'vault'`), `current` tells whether it was
 * captured for the member's current password, and `capturedAt` /
 * `captureSource` describe the stored copy. The extra fields are absent on an
 * older DS.
 */
export interface RevealMemberPasswordResponse {
  password: string;
  isLikelyAutoGenerated: boolean;
  source?: "vault" | "hubspot";
  current?: boolean | null;
  capturedAt?: string | null;
  captureSource?: PasswordCaptureSource | null;
}

/**
 * DS refusal code when the operator's FusionAuth token is missing, expired or
 * invalid while DS requires it (DS task 115): the operator must sign in again.
 */
export const PASSWORD_REVEAL_OPERATOR_UNVERIFIED =
  "PASSWORD_REVEAL_OPERATOR_UNVERIFIED";

/** DS refusal code for a reveal attempted on a DS that retired it (task 50). */
export const PASSWORD_REVEAL_RETIRED = "PASSWORD_REVEAL_RETIRED";

/**
 * The typed refusals of the reveal (ADR-011, DS tasks 111 and 115), each with
 * the HTTP status DS answers it with. A code on any other status is not
 * recognised.
 */
export const PASSWORD_REVEAL_REFUSAL_STATUS = {
  [PASSWORD_REVEAL_OPERATOR_UNVERIFIED]: 401,
  PASSWORD_NOT_STORED: 404,
  MEMBER_NOT_FOUND: 404,
  PASSWORD_REVEAL_OPERATOR_NOT_ADMIN: 403,
  PASSWORD_REVEAL_TARGET_EXCLUDED: 403,
  PASSWORD_REVEAL_RATE_LIMITED: 429,
  PASSWORD_VAULT_UNAVAILABLE: 503,
  PASSWORD_REVEAL_AUDIT_FAILED: 503,
  PASSWORD_VAULT_OPEN_FAILED: 500,
  [PASSWORD_REVEAL_RETIRED]: 410,
} as const;

export type PasswordRevealRefusalCode =
  keyof typeof PASSWORD_REVEAL_REFUSAL_STATUS;

/** Which hourly limit a 429 hit. */
export type PasswordRevealLimitScope = "operator" | "target";

/** A recognised reveal refusal: the code and, for the rate limit, the scope. */
export interface PasswordRevealRefusal {
  code: PasswordRevealRefusalCode;
  scope?: PasswordRevealLimitScope;
}

/**
 * The error DS answers once the reveal is retired: HTTP 410 with
 * `{ code: 'PASSWORD_REVEAL_RETIRED', message }` as the body.
 */
export interface PasswordRevealRetiredError {
  response: {
    status: 410;
    data: { code: typeof PASSWORD_REVEAL_RETIRED; message?: string };
  };
}
